/**
 * Formatting Utilities
 * 
 * Centralized formatting functions for file sizes, durations, 
 * and other display values used across the application.
 */

/**
 * Formats bytes into a human-readable file size string.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "0 Bytes", "1.5 KB", "32.5 MB", "1.2 GB", "2.0 TB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
/**
 * Formats a size value given in MB into a human-readable string.
 * Backend returns TotalFilesSize in MB.
 * @param {number} sizeInMB - File size in MB
 * @returns {string} Formatted size (e.g., "296.83 MB", "1.2 GB")
 */
export const formatFileSizeFromMB = (sizeInMB) => {
    if (!sizeInMB) return '0 Bytes';
    return formatFileSize(sizeInMB * 1024 * 1024);
};

/**
 * Formats bytes to GB with 1 decimal place.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "32.5 GB")
 */
export const formatFileSizeInGB = (bytes) => {
    return (bytes / (1024 ** 3)).toFixed(1) + ' GB';
};

/**
 * Formats a duration in seconds to a human-readable string.
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "0s", "45s", "3m 12s", "2h 15m 30s")
 */
export const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m ${secs}s`;
};